import React, { useEffect, useRef } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import {
  Box,
  Button,
  Chip,
  Fade,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ScheduleSendOutlinedIcon from "@mui/icons-material/ScheduleSendOutlined";
import { isTruthy, openErrorNotification } from "helpers/methods";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import { getContactSuggestion } from "../SignRequestHelper";
import {
  DeliveryChannel,
  DeliveryMode,
  RequestParticipant,
  RequestSecurity,
} from "models/interfaces";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  deleteErrorSigner,
  deleteSigner,
  removeFieldsOfCC,
  setContacts,
  setErrors,
  updateIndividualSigner,
  updateMultipleSigners,
  updateSignerOrders,
} from "redux/appSlice";
import { RootState } from "utils/store";
import DragHorizontalIcon from "../../../assets/images/DragHorizontal.svg";
import strings from "global/constants/StringConstants";
import { useFeatureState } from "hooks/use-feature";
import { useIdvSecurityOptions } from "hooks/use-idv-security-options";
import FeatureKeys from "global/constants/FeatureKeys";
import {
  getParticipantEmailError,
  getParticipantNameError,
  isValidParticipantNameLength,
} from "screens/Shared/CommonHelper";
import { normalizeOrders } from "utils/signerOrderUtils";
import {
  idvMethodsFor,
  idvSecurityKeyOf,
  idvSecurityOption,
  isIdvSecurityKey,
} from "helpers/identityVerificationHelpers";
import InPersonHandoffDialog from "screens/InPersonSigning/InPersonHandoffDialog";
import { useRequesterInPersonConsent } from "screens/InPersonSigning/useRequesterInPersonConsent";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import { embedDeliveryModeOf, isEmbedEditorMode } from "embed/EmbedEditorMode";
import { tourAttr } from "tour";
import recipientsTokens, {
  getRecipientsLevelColor,
} from "../ApproversAndSigners.tokens";
import {
  CaretDownGlyph,
  CaretUpGlyph,
  CopyGlyph,
  DragDotsGlyph,
  EyeGlyph,
  LinkGlyph,
  LockGlyph,
  MonitorGlyph,
  PenGlyph,
  ShieldGlyph,
  TickGlyph,
  TrashGlyph,
} from "./RecipientsIcons";

const ItemType = {
  SIGNER: "signer",
};

/** Internal security values, kept as-is; only their labels follow the design. */
const SECURITY = {
  none: "No security",
  otp: "OTP verification",
  code: "Security code",
  phone: "Phone authentication",
} as const;

/**
 * Design-facing labels and captions for the security dropdown — order and
 * copy match the design's `secDefs` (No security → Email OTP → SMS OTP →
 * Access Code).
 */
const SECURITY_META = [
  {
    value: SECURITY.none,
    label: "No security",
    caption: "Anyone with the link can sign",
  },
  {
    value: SECURITY.otp,
    label: "Email OTP",
    caption: "One-time code sent to the signer's email",
  },
  {
    value: SECURITY.phone,
    label: strings.smsDelivery.securityPhoneLabel,
    caption: strings.smsDelivery.securityPhoneCaption,
  },
  {
    value: SECURITY.code,
    label: "Access Code",
    caption: "Code you set and share via another channel",
  },
] as const;

/**
 * "Deliver via" channels a signer can pick (one or more). `gated` channels
 * (SMS / WhatsApp) sit behind the SMS Delivery feature; Email is always on.
 * `regionNote` is the coverage caveat shown beside the label — set only where
 * the channel can't reach every country the app otherwise accepts.
 */
const CHANNEL_META = [
  {
    value: strings.deliveryChannels.email as DeliveryChannel,
    label: strings.smsDelivery.emailLabel,
    gated: false,
    regionNote: "",
  },
  {
    value: strings.deliveryChannels.sms as DeliveryChannel,
    label: strings.smsDelivery.smsLabel,
    gated: true,
    // Matches the country picker on both phone fields in this row — see
    // `strings.SMS_SUPPORTED_COUNTRIES`.
    regionNote: strings.smsDelivery.smsRegionNote,
  },
  // WhatsApp delivery is temporarily disabled — uncomment to re-enable.
  // {
  //   value: strings.deliveryChannels.whatsApp as DeliveryChannel,
  //   label: strings.smsDelivery.whatsAppLabel,
  //   gated: true,
  // },
] as const;

const ROLE_META = [
  {
    cc: false,
    label: "Need to Sign",
    caption: "Must sign the document",
    action: "Need to Sign",
  },
  {
    cc: true,
    label: "Receives a copy",
    caption: "Gets the final document only",
    action: "Receives a Copy",
  },
] as const;

/** Delivery mode per signer (in-person signing). Values match the API. */
const DELIVERY_META = [
  {
    value: strings.deliveryModes.email,
    label: strings.inPerson.deliveryEmailLabel,
    caption: strings.inPerson.deliveryEmailCaption,
  },
  {
    value: strings.deliveryModes.inPerson,
    label: strings.inPerson.deliveryInPersonLabel,
    caption: strings.inPerson.deliveryInPersonCaption,
  },
] as const;

const styles = approversAndSignersStyles;
const { row, select } = recipientsTokens;

/**
 * Paper props for the name / email contact-suggestion dropdown. While the
 * field is showing a validation message, an EMPTY dropdown is hidden: with
 * `freeSolo` MUI keeps the popper mounted when no contact matches, and the
 * paper's border, shadow and padding turn that empty box into a thin strip
 * that sits right on top of the message. A dropdown that actually has
 * suggestions still shows, and nothing changes when there is no message.
 */
const contactSuggestionPaperProps = (errorMessage: string) => ({
  paper: {
    sx: [
      styles.contactSuggestionPaper,
      Boolean(errorMessage) && { "&:empty": { display: "none" } },
    ],
  },
});

const SignerInputFields = (props: {
  eachSigner: RequestParticipant;
  excludeIds: string[];
  setExcludeIds: Function;
  index: number;
  signerToDelay?: Partial<RequestParticipant> | Partial<RequestParticipant>[];
  setSignerToDelay?: Function;
  setIsDelaySigner?: Function;
  forBulkSend?: boolean;
  showValidInvalidTags?: boolean;
  contactList: any;
  setContactList: Function;
  groupSigner?: any;
  signers?: RequestParticipant[];
  signerOrderRequired?: boolean;
  isMobile?: boolean;
}) => {
  const {
    contactList,
    eachSigner,
    excludeIds,
    setContactList,
    setExcludeIds,
    forBulkSend,
    showValidInvalidTags,
    groupSigner,
    index,
    setIsDelaySigner,
    setSignerToDelay,
    signers,
    signerOrderRequired,
  } = props;
  // The name / email fields are visually unlabelled (placeholder only) and
  // every row looks alike, so each needs its own accessible name.
  const rowAccessibleLabel = eachSigner?.cc
    ? `CC recipient ${index + 1}`
    : `Signer ${index + 1}`;
  const dispatch = useAppDispatch();
  const { isMobile } = props;
  let { signRequest, errors, contacts } = useAppSelector(
    (state: RootState) => state.app,
  );
  const timeoutRef: any = useRef(null);
  const timeoutRef2: any = useRef(null);

  const [mobileAnchorEl, setMobileAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const mobileOpen = Boolean(mobileAnchorEl);

  const roleRef = useRef<HTMLButtonElement | null>(null);
  const securityRef = useRef<HTMLButtonElement | null>(null);
  const deliveryRef = useRef<HTMLButtonElement | null>(null);
  const [roleOpen, setRoleOpen] = React.useState(false);
  const [securityOpen, setSecurityOpen] = React.useState(false);
  const [deliveryOpen, setDeliveryOpen] = React.useState(false);
  /**
   * Requester attestation, taken the first time an in-person signer is added
   * to the account — one-time and account-level, so a cached "yes" from
   * redux skips this dialog entirely.
   */
  const [isInPersonConsentOpen, setInPersonConsentOpen] =
    React.useState<boolean>(false);
  const { ensureConsentStatus, recordConsent } = useRequesterInPersonConsent();

  // An identity verification option is a security type of its own (single
  // choice), keyed by `IDV_SECURITY_OPTIONS`.
  const [securityType, setSecurityType] = React.useState<string>(
    idvSecurityKeyOf(eachSigner.requestSecurity) ??
    (eachSigner.requestSecurity?.needSecuredAccess
      ? eachSigner.requestSecurity?.needPhoneValidation
        ? SECURITY.phone
        : eachSigner.requestSecurity?.needOTPValidation
          ? SECURITY.otp
          : SECURITY.code
      : SECURITY.none),
  );

  /**
   * SMS Delivery — the signing link can go by email, text (SMS) or WhatsApp.
   * An empty/undefined list means email-only (backward compatible).
   */
  const deliveryChannels: DeliveryChannel[] = eachSigner.deliveryChannels?.length
    ? eachSigner.deliveryChannels
    : [strings.deliveryChannels.email as DeliveryChannel];
  const hasNonEmailChannel = deliveryChannels.some(
    (c) => c !== strings.deliveryChannels.email,
  );
  const [securityCode, setSecurityCode] = React.useState<string>(
    eachSigner.requestSecurity?.securityCode || "",
  );
  /**
   * The design confirms a saved access code with a masked pill and an Edit
   * link, so a code that already exists starts out confirmed.
   */
  const [codeSaved, setCodeSaved] = React.useState<boolean>(
    Boolean(eachSigner.requestSecurity?.securityCode),
  );

  const otpState = useFeatureState(FeatureKeys.AUTH_OTP);
  const otpDisabled = otpState.status !== strings.FEATURE_STATUS_AVAILABLE;
  const otpTooltip =
    otpState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
      ? "Upgrade your plan to enable OTP verification."
      : otpState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
        ? ((otpState as any).disabledMessage ??
          "OTP verification is temporarily unavailable.")
        : "";

  const passcodeState = useFeatureState(FeatureKeys.AUTH_PASSCODE);
  const passcodeDisabled =
    passcodeState.status !== strings.FEATURE_STATUS_AVAILABLE;
  const passcodeTooltip =
    passcodeState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((passcodeState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : passcodeState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  const inPersonState = useFeatureState(FeatureKeys.ENVELOPE_IN_PERSON);
  const isInPersonAvailable =
    inPersonState.status === strings.FEATURE_STATUS_AVAILABLE;
  const inPersonTooltip =
    inPersonState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((inPersonState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : inPersonState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  // SMS Delivery gate — covers the SMS / WhatsApp delivery channels. Email
  // delivery is always available.
  const smsState = useFeatureState(FeatureKeys.ENVELOPE_SEND_SMS);
  const isSmsAvailable =
    smsState.status === strings.FEATURE_STATUS_AVAILABLE;
  const smsTooltip =
    smsState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((smsState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : smsState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  // Phone (SMS OTP) authentication is its own feature, separate from SMS
  // delivery — an auth method alongside Email OTP and Access Code.
  // Identity verification options: hidden (not locked) until the account has
  // the feature, and only for methods a vendor runs here.
  const idvOptions = useIdvSecurityOptions(
    isIdvSecurityKey(securityType) ? securityType : null,
  );

  const phoneAuthState = useFeatureState(FeatureKeys.AUTH_PHONE);
  const isPhoneAuthAvailable =
    phoneAuthState.status === strings.FEATURE_STATUS_AVAILABLE;
  const phoneAuthTooltip =
    phoneAuthState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((phoneAuthState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : phoneAuthState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";
  const isEmbed = isEmbedEditorMode();
  // In the embedded wizard a signer signs in the partner's app (EMBEDDED) unless the
  // partner pre-filled them as EMAIL; see embedDeliveryModeOf. There is no choice to
  // make there -- in person is never offered -- so the Delivery menu is hidden.
  const deliveryMode = isEmbed
    ? embedDeliveryModeOf(eachSigner.deliveryMode)
    : eachSigner.deliveryMode === strings.deliveryModes.inPerson
      ? strings.deliveryModes.inPerson
      : strings.deliveryModes.email;
  const isEmbeddedSelected = deliveryMode === strings.deliveryModes.embedded;

  const delayedRoutingState = useFeatureState(
    FeatureKeys.ENVELOPE_DELAYED_ROUTING,
  );
  const isDelayedRoutingAvailable =
    delayedRoutingState.status === strings.FEATURE_STATUS_AVAILABLE;
  const delayedRoutingTooltip =
    delayedRoutingState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((delayedRoutingState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : delayedRoutingState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "Delay";

  const handleMobileClose = () => setMobileAnchorEl(null);

  const handleRoleSelect = (option: string) => {
    setSignerToDelay?.(eachSigner);
    switch (option) {
      case "Need to Sign":
        dispatch(
          updateIndividualSigner({
            signerId: eachSigner.id,
            fieldsToUpdate: {
              cc: false,
              delayDays: undefined,
              delayHours: undefined,
              scheduleTime: undefined,
            },
          }),
        );
        break;
      case "Receives a Copy":
        setSecurityType(SECURITY.none);
        setSecurityCode("");
        setCodeSaved(false);
        dispatch(
          updateIndividualSigner({
            signerId: eachSigner.id,
            fieldsToUpdate: {
              cc: true,
              delayDays: undefined,
              delayHours: undefined,
              scheduleTime: undefined,
              requestSecurity: {
                needSecuredAccess: false,
                needOTPValidation: false,
                securityCode: null,
                otp: null,
                otpValidUntil: null,
                isVerified: false,
                verifiedAt: null,
              },
            },
          }),
        );
        dispatch(removeFieldsOfCC({ signerId: eachSigner.id }));
        break;
      case "Delay":
        if (eachSigner?.order! > 1) {
          dispatch(
            updateIndividualSigner({
              signerId: eachSigner.id,
              fieldsToUpdate: {
                delayDays: eachSigner.delayDays ?? 0,
                delayHours: eachSigner.delayHours ?? 0,
                scheduleTime: eachSigner.scheduleTime ?? null,
                cc: false,
              },
            }),
          );
          setIsDelaySigner?.(true);
        } else {
          openErrorNotification(
            "Delays cannot be added for signers with first signing order.",
          );
        }
        break;
    }
    setRoleOpen(false);
  };

  const applySecurity = (option: string, code: string) => {
    // Identity verification replaces any code or OTP: it is never combined.
    const needSecuredAccess = option !== SECURITY.none && !isIdvSecurityKey(option);
    const needOTPValidation = option === SECURITY.otp;
    const needPhoneValidation = option === SECURITY.phone;

    dispatch(
      updateIndividualSigner({
        signerId: eachSigner.id,
        fieldsToUpdate: {
          requestSecurity: {
            needSecuredAccess,
            needOTPValidation,
            needPhoneValidation,
            securityCode: option === SECURITY.code ? code || null : null,
            otp: eachSigner.requestSecurity?.otp ?? null,
            otpValidUntil: eachSigner.requestSecurity?.otpValidUntil ?? null,
            isVerified: eachSigner.requestSecurity?.isVerified ?? false,
            verifiedAt: eachSigner.requestSecurity?.verifiedAt ?? null,
            authPhone: eachSigner.requestSecurity?.authPhone ?? null,
            identityVerificationMethods: idvMethodsFor(option),
          },
        },
      }),
    );
  };

  /** Merge a change into the existing security block (auth phone / country). */
  const patchSecurity = (partial: Partial<RequestSecurity>) => {
    const current = eachSigner.requestSecurity;
    if (!current) return;
    dispatch(
      updateIndividualSigner({
        signerId: eachSigner.id,
        fieldsToUpdate: { requestSecurity: { ...current, ...partial } },
      }),
    );
  };

  // ------------------------------------------------------- delivery channels

  const toggleChannel = (channel: DeliveryChannel) => {
    const isOn = deliveryChannels.includes(channel);
    let next = isOn
      ? deliveryChannels.filter((c) => c !== channel)
      : [...deliveryChannels, channel];
    // At least one channel must stay selected.
    if (!next.length) next = deliveryChannels;
    dispatch(
      updateIndividualSigner({
        signerId: eachSigner.id,
        fieldsToUpdate: { deliveryChannels: next },
      }),
    );
  };

  const setPhone = (value: string) => {
    dispatch(
      updateIndividualSigner({
        signerId: eachSigner.id,
        fieldsToUpdate: { phone: value },
      }),
    );
  };

  const handleCopyDeliveryPhone = () => {
    patchSecurity({ authPhone: eachSigner.phone || "" });
  };

  const handleSecuritySelect = (option: string) => {
    setSecurityType(option);
    if (option !== SECURITY.code) {
      setSecurityCode("");
      setCodeSaved(false);
      applySecurity(option, "");
    } else {
      setCodeSaved(Boolean(eachSigner.requestSecurity?.securityCode));
      applySecurity(option, securityCode);
    }
    setSecurityOpen(false);
  };

  const applyDeliveryMode = (mode: string) => {
    // No remote signer to send to a vendor in person, so IDV falls back to none.
    if (mode === strings.deliveryModes.inPerson && isIdvSecurityKey(securityType)) {
      setSecurityType(SECURITY.none);
      applySecurity(SECURITY.none, "");
    }
    dispatch(
      updateIndividualSigner({
        signerId: eachSigner.id,
        fieldsToUpdate: { deliveryMode: mode as DeliveryMode },
      }),
    );
  };

  /**
   * Switching a signer to in-person is the point the decision is made, so the
   * requester's attestation is taken here rather than when the host later
   * hands the device over. Asked only once per account — a cached "yes"
   * applies the mode straight away. The mode is only applied once they
   * confirm — backing out of the dialog leaves the signer on email delivery.
   */
  const handleDeliverySelect = async (mode: string) => {
    setDeliveryOpen(false);
    if (
      mode === strings.deliveryModes.inPerson &&
      deliveryMode !== strings.deliveryModes.inPerson
    ) {
      const alreadyConsented = await ensureConsentStatus();
      if (alreadyConsented) {
        applyDeliveryMode(mode);
        return;
      }
      setInPersonConsentOpen(true);
      return;
    }
    applyDeliveryMode(mode);
  };

  const dragHandleRef = useRef<HTMLElement | null>(null);

  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ItemType.SIGNER,
    item: () => ({
      dragType: "SINGLE",
      id: eachSigner.id,
      order: eachSigner.order,
      initialOrder: eachSigner.order,
      fromGroup: groupSigner?.length > 1,
    }),
    // The design only makes rows draggable while a signing order is set.
    canDrag: () => !forBulkSend && Boolean(signerOrderRequired),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    if (!isMobile) return;
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview, isMobile]);

  const handleResetContacts = () => {
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
      dispatch(setContacts(contacts));
    }
  };

  const handleRemoveContacts = () => {
    setContactList((prevContactList: any) => {
      const updatedContactList = Object.keys(prevContactList).reduce(
        (acc: any, key: string) => {
          const { [index!]: _, ...restOfContacts } = prevContactList[key];
          return {
            ...acc,
            [key]: restOfContacts,
          };
        },
        {},
      );
      return updatedContactList;
    });
  };

  const isDelayDisabled = () =>
    !!eachSigner.delayDays ||
    !!eachSigner.delayHours ||
    !!eachSigner.scheduleTime;

  const handleDeleteSigner = () => {
    dispatch(deleteSigner({ signerId: eachSigner.id }));
    dispatch(removeFieldsOfCC({ signerId: eachSigner.id }));
    if (eachSigner.email) {
      setExcludeIds(excludeIds.filter((id) => id !== eachSigner.email));
    }
    handleRemoveContacts();
    if (!isTruthy(eachSigner.name) || !isTruthy(eachSigner.email)) {
      dispatch(setErrors({ ...errors, name: "", email: "" }));
    }
  };

  /**
   * The design moves a signer between levels with a pair of chevrons inside the
   * row. Orders are renumbered afterwards and level 1 never keeps a delay.
   */
  const allSigners: RequestParticipant[] = signers ?? signRequest.signers ?? [];
  const levelOrders: number[] = Array.from(
    new Set(allSigners.map((s) => Number(s.order))),
  ).sort((a, b) => a - b);
  const currentLevelIndex = levelOrders.indexOf(Number(eachSigner.order));
  const canMoveUp = currentLevelIndex > 0;
  const canMoveDown =
    currentLevelIndex > -1 && currentLevelIndex < levelOrders.length - 1;

  const handleLevelMove = (direction: "up" | "down") => {
    const targetIndex =
      direction === "up" ? currentLevelIndex - 1 : currentLevelIndex + 1;
    if (targetIndex < 0 || targetIndex >= levelOrders.length) return;

    const targetOrder = levelOrders[targetIndex];
    const normalized = normalizeOrders(
      allSigners.map((s: RequestParticipant) =>
        s.id === eachSigner.id ? { ...s, order: targetOrder } : s,
      ),
    );

    // Level 1 can never hold a delay, so clear it if the move lands there.
    const clears = normalized
      .filter(
        (s) =>
          s.order === 1 && (s.delayDays || s.delayHours || s.scheduleTime),
      )
      .map((s) => ({
        signerId: s.id,
        fieldsToUpdate: {
          delayDays: undefined,
          delayHours: undefined,
          scheduleTime: undefined,
        },
      }));
    if (clears.length) dispatch(updateMultipleSigners(clears));
    dispatch(updateSignerOrders(normalized));
  };

  const roleLabel = eachSigner.cc ? "Receives a copy" : "Need to Sign";
  const deliveryLabel =
    DELIVERY_META.find((meta) => meta.value === deliveryMode)?.label ??
    strings.inPerson.deliveryEmailLabel;
  const securityLabel =
    SECURITY_META.find((meta) => meta.value === securityType)?.label ??
    idvSecurityOption(securityType)?.label ??
    "No security";
  // Security (access code, one-time codes) applies to a signer in the partner's app
  // too: they are asked for it inside the iframe before the document opens.
  const showCodeEditor =
    !eachSigner.cc && securityType === SECURITY.code && !codeSaved;
  const showCodeSaved =
    !eachSigner.cc && securityType === SECURITY.code && codeSaved;
  const showOtpNote = !eachSigner.cc && securityType === SECURITY.otp;

  // SMS Delivery — channels only apply to remote (non in-person) signers.
  const isInPersonSelected =
    deliveryMode === strings.deliveryModes.inPerson;
  // Not for a signer in the partner's app: Inkfree sends them no link, the partner app opens signing.
  const showDeliverVia =
    !isEmbeddedSelected && !eachSigner.cc && !isInPersonSelected;
  // SMS / WhatsApp need a phone — captured inline in the row (between email and
  // the role dropdown), per the design.
  const showInlinePhone = showDeliverVia && hasNonEmailChannel;
  const showAuthRow = !eachSigner.cc && securityType === SECURITY.phone;
  // Email is optional when the link is only texted, unless email OTP is on.
  const emailOptional =
    isInPersonSelected ||
    (!deliveryChannels.includes(strings.deliveryChannels.email as DeliveryChannel) &&
      securityType !== SECURITY.otp);
  const phonePlaceholder =
    deliveryChannels.includes(strings.deliveryChannels.whatsApp as DeliveryChannel) &&
    !deliveryChannels.includes(strings.deliveryChannels.sms as DeliveryChannel)
      ? strings.smsDelivery.whatsAppNumberPlaceholder
      : strings.smsDelivery.phonePlaceholder;
  const canCopyDeliveryPhone =
    hasNonEmailChannel && Boolean((eachSigner.phone || "").trim());

  const barColor = getRecipientsLevelColor(
    signerOrderRequired ? Math.max(currentLevelIndex, 0) : index,
  );

  /** One row of the name/email contact-suggestion dropdown. */
  const renderContactOption = (prop: any, option: any) => (
    <Box component="li" {...prop} sx={styles.contactSuggestionItem}>
      <Box sx={styles.contactSuggestionText}>
        <Typography component="div" sx={styles.contactSuggestionName}>
          {option.name}
        </Typography>
        <Typography component="div" sx={styles.contactSuggestionEmail}>
          {option.email}
        </Typography>
      </Box>
    </Box>
  );

  const renderMenuItem = (
    key: string,
    label: string,
    caption: string,
    selected: boolean,
    onClick: () => void,
    disabled = false,
    tooltip = "",
  ) => {
    const item = (
      <MenuItem
        key={key}
        selected={selected}
        disabled={disabled}
        onClick={onClick}
        sx={styles.menuItem}
      >
        <Typography component="div" sx={styles.menuItemTitle}>
          {label}
          {disabled && (
            <LockOutlinedIcon
              sx={{ fontSize: 13, color: "text.disabled", ml: 0.75 }}
            />
          )}
        </Typography>
        <Typography component="div" sx={styles.menuItemSub}>
          {caption}
        </Typography>
      </MenuItem>
    );

    return tooltip ? (
      <Tooltip describeChild key={key} title={tooltip} placement="left" arrow>
        <Box component="span" sx={{ display: "block" }}>
          {item}
        </Box>
      </Tooltip>
    ) : (
      item
    );
  };

  // Each field's message is computed once so the same value drives both the
  // helper text and the empty-suggestion-paper rule in
  // `contactSuggestionPaperProps`.
  const nameErrorMessage =
    !isTruthy(eachSigner.name) &&
    errors.name &&
    // Mirrors the bypass on the name field's `error` prop: CustomAutoComplete
    // drives its own red styling off errorMessage, so a blank in-person
    // placeholder would still be flagged here when another signer sets
    // errors.name.
    deliveryMode !== strings.deliveryModes.inPerson
      ? "Name is required"
      : getParticipantNameError(eachSigner.name) || "";

  // The email field carried styling but never a reason, unlike the name
  // field beside it. Over-length is reported live (the same way the name
  // limit is); required/format wait for the step's own validation so the
  // field does not nag on every keystroke.
  const emailErrorMessage =
    getParticipantEmailError(eachSigner.email) ||
    (errors.email && !(emailOptional && !isTruthy(eachSigner.email))
      ? !isTruthy(eachSigner.email)
        ? "Email is required"
        : !strings.regex.test(eachSigner.email)
          ? "Please enter a valid email address"
          : ""
      : "");

  const renderNameField = () => (
    <Box sx={styles.nameField}>
      <CustomAutoComplete
        id={`signer-${eachSigner.id}-name`}
        ariaLabel={`${rowAccessibleLabel} name`}
        componentsProps={contactSuggestionPaperProps(nameErrorMessage)}
        placeHolder={
          deliveryMode === strings.deliveryModes.inPerson
            ? "Name (optional)"
            : "Enter Name"
        }
        name="name"
        freeSolo={true}
        value={eachSigner.name || ""}
        onBlur={handleResetContacts}
        onChange={async (e: any, newValue: any) => {
          if (newValue && typeof newValue === "object") {
            const updatedExcludeIds = [
              ...excludeIds.filter((id) => id !== eachSigner.email),
              newValue.email,
            ];
            setExcludeIds(updatedExcludeIds);
            dispatch(
              updateIndividualSigner({
                signerId: eachSigner.id,
                fieldsToUpdate: {
                  name: newValue.name.substring(0, 200) || "",
                  email:
                    newValue.email.substring(0, 100) ||
                    eachSigner.email.substring(0, 100),
                  phone: newValue.phone
                    ? newValue.phone.substring(0, 50)
                    : eachSigner.phone || "",
                },
              }),
            );
            getContactSuggestion(" ", dispatch, updatedExcludeIds);
          } else if (typeof newValue === "string") {
            dispatch(
              updateIndividualSigner({
                signerId: eachSigner.id,
                fieldsToUpdate: {
                  name: newValue
                    .trimStart()
                    .replace(/(?:^\s+|\s+$|\t+)/g, " ")
                    .substring(0, 200),
                },
              }),
            );
          } else if (newValue === null || newValue === "") {
            dispatch(
              updateIndividualSigner({
                signerId: eachSigner.id,
                fieldsToUpdate: { name: "" },
              }),
            );
          }
          dispatch(setErrors({ ...errors, name: "" }));
        }}
        onChangeForTextField={(
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          const { value } = e.target;
          dispatch(
            updateIndividualSigner({
              signerId: eachSigner.id,
              fieldsToUpdate: {
                name: value
                  .trimStart()
                  .replace(/(?:^\s+|\s+$|\t+)/g, " ")
                  .substring(0, 200),
              },
            }),
          );
          dispatch(setErrors({ ...errors, name: "" }));

          clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(async () => {
            if (isTruthy(value.trim())) {
              dispatch(setContacts([]));
              await getContactSuggestion(
                value.trim(),
                dispatch,
                excludeIds,
                setContactList,
                index,
                "name",
              );
            } else {
              await getContactSuggestion(
                "",
                dispatch,
                excludeIds,
                setContactList,
                index,
                "name",
              );
              dispatch(setContacts(contacts));
            }
          }, 500);
        }}
        filterOptions={(options: any, { inputValue }: any) =>
          options.filter(
            (option: any) =>
              option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.email.toLowerCase().includes(inputValue.toLowerCase()),
          )
        }
        ref={timeoutRef}
        options={(contactList.name && contactList.name[index!]
          ? contactList.name[index!]
          : contacts || []
        )?.filter(
          (c: any) =>
            !excludeIds
              .map((id: string) => id.toLowerCase())
              .includes(c.email.toLowerCase()),
        )}
        getOptionLabel={(option: any) =>
          typeof option === "string" ? option : option.name || ""
        }
        renderOption={renderContactOption}
        error={
          !(
            isTruthy(eachSigner.name) &&
            isValidParticipantNameLength(eachSigner.name)
          ) &&
          // A blank in-person placeholder is allowed — don't flag it.
          !(
            deliveryMode === strings.deliveryModes.inPerson &&
            !isTruthy(eachSigner.name)
          ) &&
          errors.name
        }
        errorMessage={nameErrorMessage}
      />
    </Box>
  );

  const renderEmailField = () => (
    <Box sx={styles.emailField}>
      <CustomAutoComplete
        id={`signer-${eachSigner.id}-email`}
        ariaLabel={`${rowAccessibleLabel} email`}
        componentsProps={contactSuggestionPaperProps(emailErrorMessage)}
        placeHolder={emailOptional ? "Enter Email (optional)" : "Enter Email"}
        name="email"
        freeSolo={true}
        value={eachSigner.email || ""}
        onBlur={handleResetContacts}
        onChange={(e: any, newValue: any) => {
          if (newValue && typeof newValue === "object") {
            const updatedExcludeIds = [
              ...excludeIds.filter((id) => id !== eachSigner.email),
              newValue.email,
            ];
            setExcludeIds(updatedExcludeIds);
            dispatch(
              updateIndividualSigner({
                signerId: eachSigner.id,
                fieldsToUpdate: {
                  name:
                    newValue.name.substring(0, 200) ||
                    eachSigner.name.substring(200),
                  email: newValue.email.substring(0, 256) || "",
                  phone: newValue.phone
                    ? newValue.phone.substring(0, 50)
                    : eachSigner.phone || "",
                },
              }),
            );
            getContactSuggestion(" ", dispatch, updatedExcludeIds);
          } else if (typeof newValue === "string") {
            dispatch(
              updateIndividualSigner({
                signerId: eachSigner.id,
                fieldsToUpdate: {
                  email: newValue.replace(/\s/g, "").substring(0, 256),
                },
              }),
            );
          } else if (newValue === null || newValue === "") {
            dispatch(
              updateIndividualSigner({
                signerId: eachSigner.id,
                fieldsToUpdate: { email: "" },
              }),
            );
          }
          dispatch(setErrors({ ...errors, email: "" }));
          handleRemoveContacts();
        }}
        onChangeForTextField={(
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          const { value } = e.target;
          dispatch(
            updateIndividualSigner({
              signerId: eachSigner.id,
              fieldsToUpdate: {
                email: value.replace(/\s/g, "").substring(0, 256),
              },
            }),
          );
          dispatch(setErrors({ ...errors, email: "" }));

          clearTimeout(timeoutRef2.current);
          timeoutRef2.current = setTimeout(() => {
            if (isTruthy(value.trim())) {
              getContactSuggestion(
                value.trim(),
                dispatch,
                excludeIds,
                setContactList,
                index,
                "email",
              );
            } else {
              dispatch(setContacts([]));
            }
          }, 500);
        }}
        filterOptions={(options: any, { inputValue }: any) =>
          options.filter(
            (option: any) =>
              option.email.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.name.toLowerCase().includes(inputValue.toLowerCase()),
          )
        }
        ref={timeoutRef2}
        options={(contactList.email && contactList.email[index!]
          ? contactList.email[index!]
          : contacts || []
        )?.filter(
          (c: any) =>
            !excludeIds
              .map((id: string) => id.toLowerCase())
              .includes(c.email.toLowerCase()),
        )}
        getOptionLabel={(option: any) =>
          typeof option === "string" ? option : option.email || ""
        }
        renderOption={renderContactOption}
        error={
          (!isTruthy(eachSigner.email) ||
            !strings.regex.test(eachSigner.email)) &&
          // A blank email is allowed when it's optional (in-person, or SMS /
          // WhatsApp only) — don't flag it. Anything entered is still checked.
          !(emailOptional && !isTruthy(eachSigner.email)) &&
          errors.email
        }
        errorMessage={emailErrorMessage}
      />
    </Box>
  );

  const renderRoleSelect = () => (
    <>
      <Button
        ref={roleRef}
        disableRipple
        id={`dropdown-signer-${eachSigner.id}`}
        aria-haspopup="true"
        aria-expanded={roleOpen ? "true" : undefined}
        onClick={() => setRoleOpen(true)}
        sx={styles.select}
      >
        {eachSigner.cc ? (
          <EyeGlyph sx={styles.selectRoleIcon(true)} />
        ) : (
          <PenGlyph sx={styles.selectRoleIcon(false)} />
        )}
        {roleLabel}
        <CaretDownGlyph sx={styles.selectCaret} />
      </Button>
      <Menu
        anchorEl={roleRef.current}
        open={roleOpen}
        onClose={() => setRoleOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ sx: { p: 0 } }}
        PaperProps={{ sx: styles.menuPaper(select.menuWidthRole) }}
      >
        {ROLE_META.map((meta) =>
          renderMenuItem(
            meta.action,
            meta.label,
            meta.caption,
            Boolean(eachSigner.cc) === meta.cc,
            () => handleRoleSelect(meta.action),
          ),
        )}
      </Menu>
    </>
  );

  const renderDeliverySelect = () => (
    <>
      <Button
        ref={deliveryRef}
        disableRipple
        id={`delivery-dropdown-signer-${eachSigner.id}`}
        aria-haspopup="true"
        aria-expanded={deliveryOpen ? "true" : undefined}
        onClick={() => setDeliveryOpen(true)}
        sx={styles.select}
      >
        {isInPersonSelected ? (
          <MonitorGlyph sx={styles.selectDeliveryIcon} />
        ) : (
          <LinkGlyph sx={styles.selectDeliveryIcon} />
        )}
        {deliveryLabel}
        <CaretDownGlyph sx={styles.selectCaret} />
      </Button>
      <Menu
        anchorEl={deliveryRef.current}
        open={deliveryOpen}
        onClose={() => setDeliveryOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ sx: { p: 0 } }}
        PaperProps={{ sx: styles.menuPaper(select.menuWidthDelivery) }}
      >
        {DELIVERY_META.map((meta) => {
          const disabled =
            meta.value === strings.deliveryModes.inPerson &&
            !isInPersonAvailable;
          return renderMenuItem(
            meta.value,
            meta.label,
            meta.caption,
            deliveryMode === meta.value,
            () => handleDeliverySelect(meta.value),
            disabled,
            disabled ? inPersonTooltip : "",
          );
        })}
      </Menu>
      {/* Records the requester's one-time, account-level consent on confirm,
          so no later hand-off or signer ever asks again. */}
      <InPersonHandoffDialog
        open={isInPersonConsentOpen}
        requestId={String(signRequest?.id ?? "")}
        variant="authoring"
        onRecordConsent={recordConsent}
        onCancel={() => setInPersonConsentOpen(false)}
        onSubmit={() => {
          applyDeliveryMode(strings.deliveryModes.inPerson);
          setInPersonConsentOpen(false);
        }}
      />
    </>
  );

  const renderSecuritySelect = () => (
    <>
      <Button
        ref={securityRef}
        disableRipple
        id={`security-dropdown-signer-${eachSigner.id}`}
        aria-haspopup="true"
        aria-expanded={securityOpen ? "true" : undefined}
        onClick={() => setSecurityOpen(true)}
        sx={styles.select}
      >
        <ShieldGlyph
          sx={styles.selectShield(securityType !== SECURITY.none)}
        />
        {securityLabel}
        <CaretDownGlyph sx={styles.selectCaret} />
      </Button>
      <Menu
        anchorEl={securityRef.current}
        open={securityOpen}
        onClose={() => setSecurityOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ sx: { p: 0 } }}
        PaperProps={{ sx: styles.menuPaper(select.menuWidthSecurity) }}
      >
        {SECURITY_META.map((meta) => {
          const disabled =
            (meta.value === SECURITY.otp && otpDisabled) ||
            (meta.value === SECURITY.code && passcodeDisabled) ||
            (meta.value === SECURITY.phone && !isPhoneAuthAvailable);
          const tooltip = disabled
            ? meta.value === SECURITY.otp
              ? otpTooltip
              : meta.value === SECURITY.code
                ? passcodeTooltip
                : phoneAuthTooltip
            : "";
          return renderMenuItem(
            meta.value,
            meta.label,
            meta.caption,
            securityType === meta.value,
            () => handleSecuritySelect(meta.value),
            disabled,
            tooltip,
          );
        })}
        {/* Identity verification: see useIdvSecurityOptions. */}
        {idvOptions.map((option) =>
            renderMenuItem(
              option.key,
              option.label,
              option.caption,
              securityType === option.key,
              () => handleSecuritySelect(option.key),
              isInPersonSelected,
              isInPersonSelected
                ? strings.identityVerification.inPersonTooltip
                : "",
            ),
          )}
      </Menu>
    </>
  );

  /** "Deliver via [ ] Email [ ] Text (SMS) [ ] WhatsApp" line inside the row. */
  const renderDeliverVia = () => (
    <Box sx={styles.deliverViaLine}>
      <Box component="span" sx={styles.deliverViaLabel}>
        {strings.smsDelivery.deliverViaLabel}
      </Box>
      {CHANNEL_META.map((ch) => {
        const on = deliveryChannels.includes(ch.value);
        const locked = ch.gated && !isSmsAvailable;
        const option = (
          <Button
            disableRipple
            disabled={locked}
            aria-pressed={on}
            onClick={() => toggleChannel(ch.value)}
            sx={styles.deliverOption}
          >
            <Box component="span" sx={styles.deliverBox(on)}>
              <TickGlyph sx={styles.deliverTick(on)} />
            </Box>
            <Box component="span" sx={styles.deliverOptionLabel}>
              {ch.label}
            </Box>
            {ch.regionNote && (
              <Box component="span" sx={styles.deliverOptionNote}>
                {ch.regionNote}
              </Box>
            )}
            {locked && <LockOutlinedIcon sx={styles.deliverLock} />}
          </Button>
        );
        return locked && smsTooltip ? (
          <Tooltip describeChild key={ch.value} title={smsTooltip} placement="top" arrow>
            <Box component="span" sx={{ display: "inline-flex" }}>
              {option}
            </Box>
          </Tooltip>
        ) : (
          <React.Fragment key={ch.value}>{option}</React.Fragment>
        );
      })}
    </Box>
  );

  /**
   * Inline "+1 ▾ | Enter Phone" field for SMS / WhatsApp — the app-wide phone
   * control (`CustomContactNumberInput`, split variant), so the number entry is
   * consistent with the rest of the app.
   */
  const renderInlinePhone = () => (
    <Box sx={styles.phoneInlineWrap}>
      <CustomContactNumberInput
        id={`signer-${eachSigner.id}-phone`}
        placeHolder={phonePlaceholder}
        value={eachSigner.phone || ""}
        countries={strings.SMS_SUPPORTED_COUNTRIES}
        onChange={(value: string) => setPhone(value)}
      />
    </Box>
  );

  const renderAuthRow = () => (
    <Box sx={styles.authRow}>
      <ShieldGlyph sx={styles.phoneIcon} />
      <Box component="span" sx={styles.phoneTitle}>
        {strings.smsDelivery.authTitle}
      </Box>
      {/* Same caveat the SMS channel carries, for the same reason: the code is
          texted, so this field is bound by the same coverage as delivery. */}
      <Box component="span" sx={styles.authNote}>
        {strings.smsDelivery.smsRegionNote}
      </Box>
      <Box component="span" sx={styles.authNote}>
        {strings.smsDelivery.authNote}
      </Box>
      <Box sx={styles.authPhoneWrap}>
        <CustomContactNumberInput
          id={`signer-${eachSigner.id}-auth-phone`}
          placeHolder={strings.smsDelivery.authPlaceholder}
          value={eachSigner.requestSecurity?.authPhone || ""}
          countries={strings.SMS_SUPPORTED_COUNTRIES}
          onChange={(value: string) => patchSecurity({ authPhone: value })}
        />
      </Box>
      {canCopyDeliveryPhone && (
        <Button
          disableRipple
          onClick={handleCopyDeliveryPhone}
          sx={styles.sameAsDelivery}
        >
          <CopyGlyph sx={styles.sameAsDeliveryIcon} />
          {strings.smsDelivery.sameAsDelivery}
        </Button>
      )}
    </Box>
  );

  const renderLevelMove = () => (
    <Box sx={styles.rowLevelMoveGroup}>
      <IconButton
        disableRipple
        aria-label="Move to earlier level"
        disabled={!canMoveUp}
        onClick={() => handleLevelMove("up")}
        sx={styles.rowLevelMove}
      >
        <CaretUpGlyph sx={styles.rowLevelMoveIcon} />
      </IconButton>
      <IconButton
        disableRipple
        aria-label="Move to later level"
        disabled={!canMoveDown}
        onClick={() => handleLevelMove("down")}
        sx={styles.rowLevelMove}
      >
        <CaretDownGlyph sx={styles.rowLevelMoveIcon} />
      </IconButton>
    </Box>
  );

  const renderCodeEditor = () => (
    <Box sx={styles.codeEditor}>
      <LockGlyph sx={styles.codeIcon} />
      <Box component="span" sx={styles.codeLabel}>
        Access code
      </Box>
      <TextField
        variant="standard"
        placeholder="Enter code shared with signer"
        value={securityCode}
        onChange={(e) => setSecurityCode(e.target.value)}
        InputProps={{ disableUnderline: true }}
        inputProps={{ "aria-label": "Access code" }}
        sx={styles.codeInput}
      />
      <Button
        disableRipple
        disabled={!securityCode.length}
        onClick={() => {
          applySecurity(SECURITY.code, securityCode);
          setCodeSaved(true);
        }}
        sx={styles.codeSave(Boolean(securityCode.length))}
      >
        Save
      </Button>
    </Box>
  );

  const renderCodeSaved = () => (
    <Box sx={styles.codeSaved}>
      <TickGlyph sx={styles.codeSavedTick} />
      <Box component="span" sx={styles.codeLabel}>
        Access code set
      </Box>
      <Box component="span" sx={styles.codeMasked}>
        {"•".repeat(securityCode.length || 4)}
      </Box>
      <Button
        disableRipple
        aria-label="Edit access code"
        onClick={() => setCodeSaved(false)}
        sx={styles.codeEdit}
      >
        Edit
      </Button>
    </Box>
  );

  const renderOtpNote = () => (
    <Box sx={styles.otpNote}>
      <ShieldGlyph sx={styles.otpNoteIcon} />
      <Box component="span" sx={styles.otpNoteText}>
        A one-time passcode will be emailed to this signer before they can view
        &amp; sign.
      </Box>
    </Box>
  );

  const renderMobileMenu = () => (
    <>
      <IconButton
        onClick={(e) => setMobileAnchorEl(e.currentTarget)}
        size="small"
        aria-label="Signer options"
        sx={{ p: 0.5, flexShrink: 0 }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={mobileAnchorEl}
        open={mobileOpen}
        onClose={handleMobileClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {forBulkSend ? (
          <MenuItem
            onClick={() => {
              if (
                isTruthy(eachSigner.name) &&
                strings.regex.test(eachSigner.email)
              ) {
                dispatch(
                  updateIndividualSigner({
                    signerId: eachSigner.id,
                    fieldsToUpdate: {
                      name: eachSigner.name,
                      email: eachSigner.email,
                      isValid: true,
                    },
                  }),
                );
                dispatch(deleteErrorSigner({ signerId: eachSigner.id }));
              } else {
                openErrorNotification("Please enter valid name/email");
              }
              handleMobileClose();
            }}
          >
            <DoneAllIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle2">Done All</Typography>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleDeleteSigner();
              handleMobileClose();
            }}
          >
            <TrashGlyph sx={{ ...styles.rowDeleteIcon, mr: 1 }} />
            <Typography variant="subtitle2">Delete</Typography>
          </MenuItem>
        )}
        {signRequest.signerOrderRequired &&
          eachSigner.order?.toString() !== "1" &&
          !eachSigner.cc &&
          !forBulkSend && (
            <Tooltip
              describeChild
              title={!isDelayedRoutingAvailable ? delayedRoutingTooltip : ""}
              placement="left"
              arrow
              disableHoverListener={isDelayedRoutingAvailable}
            >
              <Box component="span" sx={{ display: "block" }}>
                <MenuItem
                  disabled={isDelayDisabled() || !isDelayedRoutingAvailable}
                  onClick={() => {
                    handleRoleSelect("Delay");
                    handleMobileClose();
                  }}
                >
                  <ScheduleSendOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Delay</Typography>
                  {!isDelayedRoutingAvailable && (
                    <LockOutlinedIcon
                      sx={{ fontSize: 14, color: "text.disabled", ml: "auto" }}
                    />
                  )}
                </MenuItem>
              </Box>
            </Tooltip>
          )}
      </Menu>
    </>
  );

  return (
    <Box sx={styles.rowWrapper}>
      {/* The card is only the drag preview — the design makes the handle the
          draggable element, so the inputs stay selectable. */}
      <Box
        ref={dragPreview}
        sx={styles.rowCard(isDragging)}
        {...tourAttr("request.recipientRow")}
      >
        <Box sx={styles.rowBar(forBulkSend ? row.border : barColor)} />

        <Box sx={styles.rowContent}>
          <Box sx={styles.rowTop}>
        {!forBulkSend && (
          <IconButton
            disableRipple
            ref={(node) => {
              drag(node);
              dragHandleRef.current = node;
            }}
            aria-label="Reorder signer"
            title={
              signerOrderRequired ? "Drag to move to another level" : undefined
            }
            sx={styles.rowDragHandle(Boolean(signerOrderRequired))}
          >
            {isMobile ? (
              <Box component="img" src={DragHorizontalIcon} alt="" />
            ) : (
              <DragDotsGlyph sx={styles.rowDragIcon} />
            )}
          </IconButton>
        )}

        {forBulkSend && showValidInvalidTags && (
          <Chip
            label={eachSigner.isValid ? "Valid" : "Invalid"}
            sx={{
              bgcolor: eachSigner.isValid ? "success.main" : "error.main",
              color: "common.white",
              fontSize: "10px",
              height: 18,
              flexShrink: 0,
              "& .MuiChip-label": { padding: "0 6px" },
            }}
          />
        )}

        {renderNameField()}
        {renderEmailField()}

        {forBulkSend &&
          eachSigner.email &&
          !eachSigner.isValid &&
          !isMobile && (
            <IconButton
              id={`bulksend-error-contact-move-btn-${eachSigner.id}`}
              aria-label="Validate signer"
              onClick={() => {
                if (
                  isTruthy(eachSigner.name) &&
                  strings.regex.test(eachSigner.email)
                ) {
                  dispatch(
                    updateIndividualSigner({
                      signerId: eachSigner.id,
                      fieldsToUpdate: {
                        name: eachSigner.name,
                        email: eachSigner.email,
                        isValid: true,
                      },
                    }),
                  );
                  dispatch(deleteErrorSigner({ signerId: eachSigner.id }));
                } else {
                  openErrorNotification("Please enter valid name/email");
                }
              }}
              sx={{ flexShrink: 0 }}
            >
              <DoneAllIcon />
            </IconButton>
          )}

        {!forBulkSend &&
          !isMobile &&
          showInlinePhone &&
          renderInlinePhone()}

        {!forBulkSend && !isMobile && signerOrderRequired && renderLevelMove()}

        {/* Delays are added from the between-level slot, as in the design. */}

        {!forBulkSend && !isMobile && (
          <Tooltip
            title="Remove"
            slots={{ transition: Fade }}
            slotProps={{ transition: { timeout: 600 } }}
            arrow
          >
            <IconButton
              disableRipple
              id={`delete-signer-${eachSigner.id}-btn`}
              aria-label={
                `Remove signer ${eachSigner.name || eachSigner.email || ""}`.trim()
              }
              onClick={handleDeleteSigner}
              sx={styles.rowDelete}
            >
              <TrashGlyph sx={styles.rowDeleteIcon} />
            </IconButton>
          </Tooltip>
        )}

        {isMobile && renderMobileMenu()}
          </Box>

          {!forBulkSend && !isMobile && (
            <Box sx={styles.rowSettingsLine}>
              {showDeliverVia && renderDeliverVia()}
              <Box sx={styles.rowSettingsRight}>
                {renderRoleSelect()}
                {/* Always shown, like the security dropdown — "Email" stays
                    selectable and "In person" locks with a tooltip instead of
                    the whole control disappearing when the feature is gated. */}
                {!eachSigner.cc && !isEmbed && renderDeliverySelect()}
                {!eachSigner.cc && renderSecuritySelect()}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {!isMobile && showCodeEditor && renderCodeEditor()}
      {!isMobile && showCodeSaved && renderCodeSaved()}
      {!isMobile && showOtpNote && renderOtpNote()}
      {!isMobile && showAuthRow && renderAuthRow()}

      {!isTruthy(eachSigner.order) &&
        signRequest.signerOrderRequired &&
        errors.order && (
          <FormHelperText error sx={{ ml: `${row.barWidth + 22}px` }}>
            {errors.order}
          </FormHelperText>
        )}
    </Box>
  );
};

export default SignerInputFields;
